import React, { useEffect, useState } from "react";
import Swal from "sweetalert";
import { createCategory, getAllCategories } from "../../network/api";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Image,
  Package,
  Edit3,
  Eye,
  Download,
  Star,
  Trash2,
  Crown,
  HardDrive,
  FolderTree,
  Sticker,
} from "lucide-react";
import { DOMAIN } from "../../strings/CONSTANTS";

const Sticker_Categories: React.FC = () => {
  const [categories, setCategories] = useState<any[]>([]);
  const [packsByCategory, setPacksByCategory] = useState<any>({});
  const [name, setName] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const [currentPage, setCurrentPage] = useState<any>({});
  const PACKS_PER_PAGE = 6;

  const navigate = useNavigate();

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const data = await getAllCategories();
      setCategories(data || []);
      fetchAllPacks();
    } catch (err) {
      console.log("Fetch error:", err);
    }
  };

  // Fetch sticker packs + group by category
  const fetchAllPacks = async () => {
    try {
      const res = await axios.get(`${DOMAIN}/sticker/list`);
      const packs = res.data.packs || [];

      const grouped: any = {};
      packs.forEach((pack: any) => {
        const catId = pack.categoryId._id;
        if (!grouped[catId]) grouped[catId] = [];
        grouped[catId].push(pack);
      });

      setPacksByCategory(grouped);
    } catch (err) {
      console.log("Sticker pack load error:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Upload image
  const handleImageUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result?.toString() || "";
      setImagePreview(result);
      setImageBase64(result.split(",")[1] || "");
    };
    reader.readAsDataURL(file);
  };

  // Create Category
  const handleCreateCategory = async () => {
    if (!name || !imageBase64) {
      Swal("Error", "Category name & image are required", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await createCategory({ name, imageBase64 });

      if (res.success) {
        Swal("Success", "Category created successfully", "success");
        setName("");
        setImageBase64("");
        setImagePreview("");
        setShowAddForm(false);
        fetchCategories();
      } else {
        Swal("Error", res.message || "Something went wrong", "error");
      }
    } catch (err) {
      Swal("Error", "Failed to create category", "error");
    }
    setLoading(false);
  };

  // Toggle Featured
  const toggleFeatured = async (packId: string, current: boolean) => {
    try {
      const res = await axios.put(`${DOMAIN}/sticker/update/${packId}`, {
        isFeatured: !current,
      });

      if (res.data.success) {
        Swal("Updated", "Featured status updated", "success");
        fetchAllPacks();
      }
    } catch (error) {
      Swal("Error", "Unable to update featured status", "error");
    }
  };

  // Delete PACK
  const deletePack = async (id: string) => {
    Swal({
      title: "Delete Sticker Pack?",
      text: "This action cannot be undone!",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (willDelete: any) => {
      if (willDelete) {
        try {
          await axios.delete(`${DOMAIN}/sticker/delete/${id}`);
          Swal("Deleted!", "Sticker pack removed", "success");
          fetchAllPacks();
        } catch {
          Swal("Error", "Failed to delete pack", "error");
        }
      }
    });
  };

  const formatSize = (sizeInMB: number) => {
    if (!sizeInMB || sizeInMB === 0) return null;
    if (sizeInMB < 0.01) return "< 0.01 MB";
    return `${sizeInMB.toFixed(2)} MB`;
  };

  const getPaginatedPacks = (catId: string) => {
    const packs = packsByCategory[catId] || [];

    const page = currentPage[catId] || 1;
    const start = (page - 1) * PACKS_PER_PAGE;
    const end = start + PACKS_PER_PAGE;

    return packs.slice(start, end);
  };

  const getTotalPages = (catId: string) => {
    const packs = packsByCategory[catId] || [];
    return Math.ceil(packs.length / PACKS_PER_PAGE);
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header - Green Theme */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 shadow-lg">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FolderTree className="w-6 h-6" />
              Sticker Categories
            </h1>
            <p className="text-green-100 mt-1 text-sm">
              Manage your sticker collections and categories
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Create Stickers Button */}
            <button
              onClick={() => navigate("/stickers/create")}
              className="flex items-center gap-2 bg-white text-green-700 px-5 py-2.5 rounded-xl hover:bg-green-50 transition shadow-lg font-medium"
            >
              <Plus size={18} />
              Create Stickers
            </button>

            {/* Add Category Button */}
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-5 py-2.5 rounded-xl hover:bg-white/20 transition border border-white/20 font-medium"
            >
              <Plus size={18} />
              Add Category
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500 hover:shadow-md transition">
          <p className="text-sm text-gray-500">Total Categories</p>
          <p className="text-2xl font-bold text-gray-900">
            {categories.length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500 hover:shadow-md transition">
          <p className="text-sm text-gray-500">Total Sticker Packs</p>
          <p className="text-2xl font-bold text-gray-900">
            {Object.values(packsByCategory).reduce(
              (acc: number, packs: any) => acc + packs.length,
              0
            )}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-500 hover:shadow-md transition">
          <p className="text-sm text-gray-500">Featured Packs</p>
          <p className="text-2xl font-bold text-gray-900">
            {Object.values(packsByCategory).reduce(
              (acc: number, packs: any) =>
                acc + packs.filter((p: any) => p.isFeatured).length,
              0
            )}
          </p>
        </div>
      </div>

      {/* ADD CATEGORY FORM */}
      {showAddForm && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Plus size={20} />
              Create New Category
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Name + Image */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                    placeholder="Enter category name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Image
                  </label>

                  <label
                    htmlFor="image-upload"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-green-500 hover:bg-green-50 transition"
                  >
                    <Image size={20} className="text-gray-400" />
                    <span className="text-gray-600">
                      {imagePreview ? "Change Image" : "Choose Image"}
                    </span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id="image-upload"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="flex items-center justify-center">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-48 h-48 object-cover rounded-2xl shadow-md border-4 border-green-600"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300">
                    <div className="text-center">
                      <Image size={48} className="text-gray-400 mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">
                        No image selected
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateCategory}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg hover:bg-green-700 transition disabled:opacity-50 font-medium"
                disabled={loading}
              >
                {loading ? "Creating..." : "Create Category"}
              </button>

              <button
                onClick={() => {
                  setShowAddForm(false);
                  setName("");
                  setImageBase64("");
                  setImagePreview("");
                }}
                className="px-6 py-3 bg-gray-100 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CATEGORY LIST */}
      <div className="space-y-6">
        {categories.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <FolderTree size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No categories yet
            </h3>
            <p className="text-gray-500">
              Create your first category to get started
            </p>
          </div>
        ) : (
          categories.map((cat) => (
            <div
              key={cat._id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
            >
              {/* CATEGORY HEADER */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-5 border-b border-green-500/20">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={`${cat.image}`}
                      alt={cat.name}
                      className="w-20 h-20 object-cover rounded-xl shadow-md border-4 border-white/20"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold shadow-lg">
                      {packsByCategory[cat._id]?.length || 0}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-white">{cat.name}</h3>
                    <p className="text-green-100 flex items-center gap-2 mt-1 text-sm">
                      <Sticker size={16} />{" "}
                      {packsByCategory[cat._id]?.length || 0} Sticker Packs
                    </p>
                  </div>
                </div>
              </div>

              {/* STICKER PACKS LIST */}
              {packsByCategory[cat._id]?.length > 0 ? (
                <>
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getPaginatedPacks(cat._id).map((pack: any) => {
                      const packSize = pack.size || 0;
                      const formattedSize = formatSize(packSize);

                      return (
                        <div
                          key={pack._id}
                          className="group bg-white rounded-xl border border-gray-100 p-4 hover:border-green-400 hover:shadow-lg transition-all"
                        >
                          {/* HEADER WITH BADGES */}
                          <div className="flex justify-between mb-3">
                            <div className="flex gap-2 flex-wrap">
                              {/* Animated Badge */}
                              {pack.type === "animated" && (
                                <span className="px-2.5 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-xs text-white rounded-full flex gap-1 items-center font-semibold shadow-sm">
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                  Animated
                                </span>
                              )}

                              {/* Premium Badge */}
                              {pack.isPremium && (
                                <span className="px-2.5 py-1 bg-gradient-to-r from-purple-500 to-purple-700 text-xs text-white rounded-full flex gap-1 items-center font-semibold shadow-sm">
                                  <Crown size={12} /> Premium
                                </span>
                              )}

                              {/* Featured Badge */}
                              {pack.isFeatured && !pack.isPremium && (
                                <span className="px-2.5 py-1 bg-yellow-500 text-xs text-white rounded-full flex gap-1 items-center font-semibold shadow-sm">
                                  <Star size={12} /> Featured
                                </span>
                              )}

                              {/* Premium + Featured */}
                              {pack.isFeatured && pack.isPremium && (
                                <span className="px-2.5 py-1 bg-gradient-to-r from-purple-500 to-yellow-500 text-xs text-white rounded-full flex gap-1 items-center font-semibold shadow-sm">
                                  <Crown size={12} /> Premium <Star size={10} />
                                </span>
                              )}
                            </div>

                            {/* Delete button */}
                            <button
                              onClick={() => deletePack(pack._id)}
                              className="text-red-400 hover:text-red-600 transition"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>

                          {/* THUMBNAIL */}
                          <div className="flex items-start gap-3 mb-3">
                            <img
                              src={`${pack.mainImage}`}
                              className="w-16 h-16 object-cover rounded-xl shadow-sm border border-gray-200"
                              alt={pack.title}
                            />

                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-gray-900 text-sm truncate">
                                {pack.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {pack.stickers?.length || 0} stickers
                              </p>

                              {formattedSize && (
                                <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                  <HardDrive size={12} />
                                  <span>{formattedSize}</span>
                                </div>
                              )}

                              <div className="flex gap-3 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Eye size={12} /> {pack.totalViews}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Download size={12} /> {pack.totalDownloads}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* STICKER PREVIEW GRID */}
                          <div className="grid grid-cols-6 gap-1.5 mb-3">
                            {pack.stickers
                              .slice(0, 6)
                              .map((img: string, i: number) => (
                                <div
                                  key={i}
                                  className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-green-500 transition"
                                >
                                  <img
                                    src={`${img}`}
                                    className="w-full h-full object-cover"
                                    alt=""
                                  />
                                </div>
                              ))}
                          </div>

                          {/* ACTION BUTTONS */}
                          <div className="flex gap-2">
                            <button
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-sm text-sm font-medium"
                              onClick={() =>
                                navigate(`/stickers/edit/${pack._id}`)
                              }
                            >
                              <Edit3 size={14} />
                              Edit
                            </button>

                            <button
                              onClick={() =>
                                toggleFeatured(pack._id, pack.isFeatured)
                              }
                              className={`px-3 py-2 rounded-lg text-white shadow-sm text-sm font-medium transition ${
                                pack.isFeatured
                                  ? "bg-yellow-500 hover:bg-yellow-600"
                                  : "bg-gray-400 hover:bg-gray-500"
                              }`}
                            >
                              {pack.isFeatured ? "Unfeature" : "Feature"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="p-12 text-center">
                  <Sticker size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    No sticker packs in this category yet
                  </p>
                </div>
              )}

              {/* PAGINATION */}
              {getTotalPages(cat._id) > 1 && (
                <div className="flex justify-center items-center gap-2 pb-6">
                  {Array.from({ length: getTotalPages(cat._id) }).map(
                    (_, i) => (
                      <button
                        key={i}
                        onClick={() =>
                          setCurrentPage((prev: any) => ({
                            ...prev,
                            [cat._id]: i + 1,
                          }))
                        }
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                          (currentPage[cat._id] || 1) === i + 1
                            ? "bg-green-600 text-white"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                        }`}
                      >
                        {i + 1}
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Sticker_Categories;
